import { useEffect, useState, useMemo } from 'react';
import { toast } from 'sonner';
import api, { downloadFile, getMediaBlobUrl } from '../../utils/api';
import { 
  BookOpen, Download, FolderOpen, Search, FileText, Eye, X, Loader2, Filter, BookMarked, FileSpreadsheet, ScrollText
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import BackButton from '../../components/BackButton';

// Modern category configuration with icons and colors
const CATEGORIES = {
  'Books': { icon: BookMarked, gradient: 'from-violet-500 to-purple-600', bg: 'bg-violet-500', light: 'bg-violet-50', text: 'text-violet-600', border: 'border-violet-200' },
  'Reports': { icon: FileSpreadsheet, gradient: 'from-blue-500 to-indigo-600', bg: 'bg-blue-500', light: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200' },
  'Policies': { icon: ScrollText, gradient: 'from-emerald-500 to-teal-600', bg: 'bg-emerald-500', light: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200' },
  'Publications': { icon: FileText, gradient: 'from-amber-500 to-orange-600', bg: 'bg-amber-500', light: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200' },
  'Forms': { icon: FileText, gradient: 'from-rose-500 to-pink-600', bg: 'bg-rose-500', light: 'bg-rose-50', text: 'text-rose-600', border: 'border-rose-200' },
  'Guides': { icon: BookOpen, gradient: 'from-cyan-500 to-sky-600', bg: 'bg-cyan-500', light: 'bg-cyan-50', text: 'text-cyan-600', border: 'border-cyan-200' },
  'Resources': { icon: FolderOpen, gradient: 'from-slate-500 to-gray-600', bg: 'bg-slate-500', light: 'bg-slate-50', text: 'text-slate-600', border: 'border-slate-200' },
  'Other': { icon: FileText, gradient: 'from-gray-500 to-slate-600', bg: 'bg-gray-500', light: 'bg-gray-50', text: 'text-gray-600', border: 'border-gray-200' },
};

const getCategoryConfig = (cat) => CATEGORIES[cat] || CATEGORIES['Other'];

const Library = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Preview
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  
  // Filter & Search
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');

  useEffect(() => { loadItems(); }, []);

  const loadItems = async () => {
    try {
      const response = await api.get('/library');
      setItems(response.data);
    } catch (error) {
      toast.error('Failed to load library items');
    } finally {
      setLoading(false);
    }
  };

  // Filtered items
  const filteredItems = useMemo(() => {
    return items.filter(item => {
      const matchesSearch = item.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
                           (item.description && item.description.toLowerCase().includes(searchQuery.toLowerCase()));
      const matchesCategory = selectedCategory === 'all' || item.category === selectedCategory;
      return matchesSearch && matchesCategory;
    });
  }, [items, searchQuery, selectedCategory]);

  // Group by category
  const groupedItems = useMemo(() => {
    return filteredItems.reduce((acc, item) => {
      const cat = item.category;
      if (!acc[cat]) acc[cat] = [];
      acc[cat].push(item);
      return acc;
    }, {});
  }, [filteredItems]);

  // Stats
  const stats = useMemo(() => ({
    total: items.length,
    books: items.filter(i => i.category === 'Books').length,
    reports: items.filter(i => i.category === 'Reports').length,
    policies: items.filter(i => i.category === 'Policies').length,
  }), [items]);

  const openPreview = async (item) => {
    setPreviewItem(item);
    setPreviewOpen(true);
    setPreviewLoading(true);
    try {
      const url = await getMediaBlobUrl(item.file_url);
      setPreviewUrl(url);
    } catch (error) {
      toast.error('Failed to load preview');
    } finally {
      setPreviewLoading(false);
    }
  };

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewItem(null);
    if (previewUrl) { window.URL.revokeObjectURL(previewUrl); setPreviewUrl(null); }
  };

  const handleDownload = async (item) => {
    try {
      await downloadFile(item.file_url, item.title);
      toast.success('Download started');
    } catch (error) {
      toast.error('Download failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="member-library-page">
      <BackButton to="/member" />
      
      {/* Header */}
      <div className="flex items-center space-x-4">
        <div className="bg-gradient-to-br from-violet-500 to-purple-600 p-4 rounded-2xl shadow-lg">
          <BookOpen className="w-8 h-8 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-serif font-bold text-slate-900">Library</h1>
          <p className="text-slate-600">Books, reports, policies, and publications</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-gradient-to-br from-violet-50 to-purple-50 border border-violet-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-violet-600 text-sm font-medium">Total Documents</p>
              <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
            </div>
            <BookOpen className="w-8 h-8 text-violet-300" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-blue-600 text-sm font-medium">Books</p>
              <p className="text-2xl font-bold text-slate-900">{stats.books}</p>
            </div>
            <BookMarked className="w-8 h-8 text-blue-300" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border border-emerald-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-emerald-600 text-sm font-medium">Reports</p>
              <p className="text-2xl font-bold text-slate-900">{stats.reports}</p>
            </div>
            <FileSpreadsheet className="w-8 h-8 text-emerald-300" />
          </div>
        </div>
        <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-amber-600 text-sm font-medium">Policies</p>
              <p className="text-2xl font-bold text-slate-900">{stats.policies}</p>
            </div>
            <ScrollText className="w-8 h-8 text-amber-300" />
          </div>
        </div>
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col sm:flex-row gap-4 p-4 bg-white rounded-xl border border-slate-200 shadow-sm">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} placeholder="Search documents..." className="pl-10 border-slate-200" />
          {searchQuery && <button onClick={() => setSearchQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>}
        </div>
        <div className="flex items-center space-x-2">
          <Filter className="w-5 h-5 text-slate-400" />
          <Select value={selectedCategory} onValueChange={setSelectedCategory}>
            <SelectTrigger className="w-48 border-slate-200"><SelectValue placeholder="All Categories" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {Object.keys(CATEGORIES).map(cat => <SelectItem key={cat} value={cat}>{cat}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Category Pills */}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => setSelectedCategory('all')} className={`px-4 py-2 rounded-full text-sm font-medium transition-all ${selectedCategory === 'all' ? 'bg-gradient-to-r from-violet-500 to-purple-600 text-white shadow-lg' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>All</button>
        {Object.keys(CATEGORIES).map(cat => {
          const config = getCategoryConfig(cat);
          const count = items.filter(i => i.category === cat).length;
          return (
            <button key={cat} onClick={() => setSelectedCategory(cat)} className={`px-4 py-2 rounded-full text-sm font-medium transition-all flex items-center space-x-2 ${selectedCategory === cat ? `bg-gradient-to-r ${config.gradient} text-white shadow-lg` : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
              <span>{cat}</span>
              <span className={`text-xs ${selectedCategory === cat ? 'bg-white/20' : 'bg-slate-200'} px-2 py-0.5 rounded-full`}>{count}</span>
            </button>
          );
        })}
      </div>

      {/* Preview Modal */}
      {previewOpen && previewItem && (
        <div className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center p-4" onClick={closePreview}>
          <div className="relative max-w-4xl w-full max-h-[90vh] bg-white rounded-2xl overflow-hidden shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <div>
                <h3 className="font-semibold text-slate-900">{previewItem.title}</h3>
                <p className="text-sm text-slate-500">{previewItem.category}</p>
              </div>
              <div className="flex items-center space-x-2">
                <Button onClick={() => handleDownload(previewItem)} className="bg-violet-600 hover:bg-violet-700">
                  <Download className="w-4 h-4 mr-2" />Download
                </Button>
                <button onClick={closePreview} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="h-[70vh] bg-slate-50">
              {previewLoading ? (
                <div className="h-full flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-violet-500" /></div>
              ) : previewUrl ? (
                <iframe src={previewUrl} className="w-full h-full" title="Document Preview" />
              ) : (
                <div className="h-full flex items-center justify-center text-slate-400">Preview not available. Click Download to view.</div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Content Grid by Category */}
      {loading ? (
        <div className="flex items-center justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-violet-500" /></div>
      ) : Object.keys(groupedItems).length > 0 ? (
        <div className="space-y-8">
          {Object.keys(groupedItems).map((cat) => {
            const config = getCategoryConfig(cat);
            const IconComponent = config.icon;
            return (
              <div key={cat}>
                <div className="flex items-center space-x-3 mb-4">
                  <div className={`bg-gradient-to-r ${config.gradient} p-2 rounded-lg shadow-lg`}>
                    <IconComponent className="w-5 h-5 text-white" />
                  </div>
                  <h2 className="text-xl font-serif font-semibold text-slate-900">{cat}</h2>
                  <span className="text-sm text-slate-500">({groupedItems[cat].length})</span>
                  <div className="h-px flex-1 bg-gradient-to-r from-slate-200 to-transparent" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {groupedItems[cat].map((item) => (
                    <div key={item.id} className={`group bg-white rounded-2xl overflow-hidden border-2 ${config.border} hover:shadow-xl transition-all duration-300`}>
                      <div className={`h-2 bg-gradient-to-r ${config.gradient}`} />
                      <div className="p-5">
                        <div className="flex items-start mb-4">
                          <div className={`${config.light} p-3 rounded-xl`}>
                            <IconComponent className={`w-6 h-6 ${config.text}`} />
                          </div>
                        </div>
                        <h3 className={`font-semibold text-lg text-slate-900 mb-2 line-clamp-2 group-hover:${config.text} transition-colors`}>{item.title}</h3>
                        {item.description && <p className="text-sm text-slate-600 mb-4 line-clamp-2">{item.description}</p>}
                        <div className="flex space-x-2">
                          <Button onClick={() => openPreview(item)} variant="outline" size="sm" className="flex-1">
                            <Eye className="w-4 h-4 mr-1" />Preview
                          </Button>
                          <Button onClick={() => handleDownload(item)} size="sm" className={`flex-1 bg-gradient-to-r ${config.gradient} text-white`}>
                            <Download className="w-4 h-4 mr-1" />Download
                          </Button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <div className="bg-white border border-slate-200 rounded-2xl text-center py-16">
          <BookOpen className="w-16 h-16 mx-auto mb-4 text-slate-300" />
          <p className="text-slate-600 text-lg">{selectedCategory === 'all' ? 'No documents available yet' : `No documents in ${selectedCategory}`}</p>
          <p className="text-sm text-slate-500 mt-2">Check back later for resources</p>
        </div>
      )}
    </div>
  );
};

export default Library;
